import { useMemo } from 'react';
import { useUser } from '@reducers/user/hooks';
import { useFeatureFlagEnabled } from './hooks';
import { FeatureFlagConfig } from './types';

export interface FeatureFlagGuardProps extends FeatureFlagConfig {
  fallback?: React.ReactNode;
  children: React.ReactNode;
  allowedRoles?: string[];
}

/**
 * FeatureFlagGuard component
 @example
 <FeatureFlagGuard featureName="feature-name">
  <Component />
 </FeatureFlagGuard>
 */

const FeatureFlagGuard = ({
  featureName,
  fallback = null,
  children,
  allowedRoles = [],
}: FeatureFlagGuardProps) => {
  const isEnabled = useFeatureFlagEnabled({ featureName });

  const { role } = useUser();

  const hasRolePermission = useMemo(
    () => allowedRoles.length === 0 || allowedRoles.includes(role),
    [role, allowedRoles],
  );

  if (!isEnabled || !hasRolePermission) {
    return fallback;
  }

  return children;
};

export default FeatureFlagGuard;
